/**
 * Tiered recovery (`account-security`) sessions: the recovery inbox,
 * the user editor's reset / request actions, and the signed-in user's
 * pending sign-in approvals. Every step-up proof comes from a `StepUpFlow`
 * the adapter runs; these own the calls, derived labels and error mapping.
 */

import { accountErrorCode, accountErrorMessage, type RecoveryApi, type AccountSecurityApi } from '@dealr/api-account';
import type {
  RecoveryApprovalRole,
  RecoveryRequest,
  RecoveryRequestType,
  SessionApproval,
  StepUpProof,
} from '@dealr/core-account/domain';
import {
  approvalRoleLabel,
  approvalsRemaining,
  formatHoldCountdown,
  holdRemainingMs,
  identityCheckLabel,
  identityCheckTone,
  missingLabel,
  recoveryActions,
  recoveryRequestDraft,
  recoveryStatusLabel,
  recoveryStatusTone,
  recoveryTierLabel,
  recoveryTypeLabel,
  validateCallbackNote,
  validateIdentityConfirmationNote,
  type RecoveryActions,
  type RecoveryTone,
  type RecoveryViewer,
  nameMatchView,
  type NameMatchView,
  SESSION_APPROVAL_WRONG_NUMBER,
} from '@dealr/core-account';
import { createStateBox, type Observable } from './store';
import { flowErrorMessage } from './stepUp';

export interface RecoveryRequestView {
  request: RecoveryRequest;
  typeLabel: string;
  tierLabel: string;
  statusLabel: string;
  statusTone: RecoveryTone;
  identityLabel: string;
  identityTone: RecoveryTone;
  approvalsRemaining: number;
  missing: string[];
  holdCountdown: string;
  actions: RecoveryActions;
  approveOptions: Array<{ role: RecoveryApprovalRole; label: string }>;
  /** `account-security`: the ID-name advisory, null until the ID check passed. */
  nameMatch: NameMatchView | null;
}

export function recoveryRequestView(request: RecoveryRequest, viewer: RecoveryViewer, now: number): RecoveryRequestView {
  const actions = recoveryActions(request, viewer);
  return {
    request,
    typeLabel: recoveryTypeLabel(request.type),
    tierLabel: recoveryTierLabel(request.tier),
    statusLabel: recoveryStatusLabel(request.status),
    statusTone: recoveryStatusTone(request.status),
    identityLabel: identityCheckLabel(request.identity_check),
    identityTone: identityCheckTone(request.identity_check),
    approvalsRemaining: approvalsRemaining(request),
    missing: (request.missing || []).map(missingLabel),
    holdCountdown: formatHoldCountdown(holdRemainingMs(request, now)),
    actions,
    approveOptions: actions.approveAs.map((role) => ({ role, label: approvalRoleLabel(role) })),
    nameMatch: nameMatchView(request.name_match),
  };
}

export interface RecoveryInboxState {
  filter: 'open' | 'all';
  loading: boolean;
  error: string;
  /** The result of the last command, for a live region. */
  announcement: string;
  busyUuid: string | null;
  requests: RecoveryRequestView[];
}

export interface RecoveryInbox extends Observable<RecoveryInboxState> {
  load(filter?: 'open' | 'all'): Promise<void>;
  /** Re-reads one request (also pulls a pending ID-check result). */
  refresh(uuid: string): Promise<void>;
  /** Validates what the approval needs before the adapter asks for a step-up; null when ready. */
  approvalProblem(role: RecoveryApprovalRole, note: string): string | null;
  /** Runs inside a `StepUpFlow` action: a rejection propagates so the flow shows it. */
  approve(uuid: string, role: RecoveryApprovalRole, proof: StepUpProof, note?: string): Promise<void>;
  deny(uuid: string, note?: string): Promise<void>;
  cancel(uuid: string): Promise<void>;
  sendIdentityCheck(uuid: string, channel: 'email' | 'sms'): Promise<void>;
  /** Validates the identity-confirmation note before the adapter asks for a step-up; null when ready. */
  identityConfirmationProblem(note: string): string | null;
  /** Runs inside a `StepUpFlow` action: a dealership admin confirms identity instead of the ID check. */
  confirmIdentity(uuid: string, proof: StepUpProof, note: string): Promise<void>;
  /** Recomputes hold countdowns; the adapter calls it on its own timer. */
  tick(): void;
}

export function createRecoveryInbox(api: RecoveryApi, viewer: RecoveryViewer, now: () => number): RecoveryInbox {
  const box = createStateBox<RecoveryInboxState>({ filter: 'open', loading: false, error: '', announcement: '', busyUuid: null, requests: [] });
  const view = (request: RecoveryRequest) => recoveryRequestView(request, viewer, now());
  const replace = (request: RecoveryRequest) =>
    box.set({ requests: box.state.requests.map((v) => (v.request.uuid === request.uuid ? view(request) : v)) });

  async function command(uuid: string, call: () => Promise<{ request: RecoveryRequest }>, done: string, fallback: string): Promise<void> {
    if (box.state.busyUuid) return;
    box.set({ busyUuid: uuid, error: '' });
    try {
      replace((await call()).request);
      box.set({ busyUuid: null, announcement: done });
    } catch (error) {
      box.set({ busyUuid: null, error: flowErrorMessage(error, fallback) });
    }
  }

  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    async load(filter = box.state.filter) {
      box.set({ filter, loading: true, error: '' });
      try {
        const response = await api.list(filter);
        box.set({ loading: false, requests: (response.requests || []).map(view) });
      } catch (error) {
        box.set({ loading: false, error: flowErrorMessage(error, 'Recovery requests could not be loaded.') });
      }
    },
    async refresh(uuid) {
      try {
        replace((await api.get(uuid)).request);
      } catch (error) {
        box.set({ error: flowErrorMessage(error, 'This request could not be loaded.') });
      }
    },
    approvalProblem(role, note) {
      return role === 'callback' ? validateCallbackNote(note) : null;
    },
    async approve(uuid, role, proof, note) {
      const body: { role: RecoveryApprovalRole; step_up: StepUpProof; note?: string } = { role, step_up: proof };
      if (note && note.trim()) body.note = note.trim();
      const response = await api.approve(uuid, body);
      replace(response.request);
      box.set({ announcement: 'Approval recorded.' });
    },
    deny(uuid, note) {
      return command(uuid, () => api.deny(uuid, note && note.trim() ? note.trim() : undefined), 'Request denied.', 'The request could not be denied.');
    },
    cancel(uuid) {
      return command(uuid, () => api.cancel(uuid), 'Request cancelled.', 'The request could not be cancelled.');
    },
    sendIdentityCheck(uuid, channel) {
      return command(uuid, () => api.sendIdentityCheck(uuid, channel), 'ID check link sent.', 'The ID check could not be sent.');
    },
    identityConfirmationProblem(note) {
      return validateIdentityConfirmationNote(note);
    },
    async confirmIdentity(uuid, proof, note) {
      const response = await api.confirmIdentity(uuid, { step_up: proof, note: note.trim() });
      replace(response.request);
      box.set({ announcement: 'Identity confirmation recorded.' });
    },
    tick() {
      box.set({ requests: box.state.requests.map((v) => view(v.request)) });
    },
  };
}

export interface UserRecoveryState {
  error: string;
  /** The request the last action created or completed. */
  result: RecoveryRequestView | null;
  announcement: string;
}

/** The user editor's tiered actions for one target user. */
export interface UserRecoveryActions extends Observable<UserRecoveryState> {
  /** Validates a request before the adapter asks for a step-up. */
  draftProblem(input: { type: RecoveryRequestType; value?: string }): string | null;
  /** Inside a `StepUpFlow` action: tier-2 in-app MFA reset. */
  resetMfa(proof: StepUpProof): Promise<void>;
  /** Inside a `StepUpFlow` action: start a recovery request. */
  requestRecovery(input: { type: RecoveryRequestType; value?: string; reason?: string }, proof: StepUpProof): Promise<void>;
  clear(): void;
}

export function createUserRecoveryActions(
  api: RecoveryApi,
  userId: number | string,
  viewer: RecoveryViewer,
  now: () => number,
): UserRecoveryActions {
  const box = createStateBox<UserRecoveryState>({ error: '', result: null, announcement: '' });
  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    draftProblem(input) {
      const draft = recoveryRequestDraft(input);
      return 'error' in draft ? draft.error : null;
    },
    async resetMfa(proof) {
      const response = await api.resetMfa(userId, proof);
      box.set({ error: '', result: recoveryRequestView(response.request, viewer, now()), announcement: 'Two-step verification reset.' });
    },
    async requestRecovery(input, proof) {
      const draft = recoveryRequestDraft(input);
      if ('error' in draft) throw new Error(draft.error);
      const response = await api.createRequest(userId, { ...draft.body, step_up: proof });
      box.set({ error: '', result: recoveryRequestView(response.request, viewer, now()), announcement: 'Recovery request created.' });
    },
    clear() {
      box.set({ error: '', result: null, announcement: '' });
    },
  };
}

export interface SessionApprovalsState {
  approvals: SessionApproval[];
  error: string;
  busyUuid: string | null;
  announcement: string;
}

/** How an approve ended: approved, or denied because the picked number was wrong. */
export type SessionApprovalOutcome = 'approved' | 'wrong_number';

export interface SessionApprovals extends Observable<SessionApprovalsState> {
  load(): Promise<void>;
  /**
   * Inside a `StepUpFlow` action, with the number the user picked from the row's choices.
   * A wrong number denies the request: it resolves `wrong_number` (so the step-up closes),
   * drops the row and shows why. Other failures reject, for the step-up to show.
   */
  approve(uuid: string, proof: StepUpProof, match: number): Promise<SessionApprovalOutcome>;
  deny(uuid: string): Promise<void>;
  setBusy(uuid: string | null): void;
}

export function createSessionApprovals(
  api: Pick<AccountSecurityApi, 'sessionApprovals' | 'approveSessionApproval' | 'denySessionApproval'>,
): SessionApprovals {
  const box = createStateBox<SessionApprovalsState>({ approvals: [], error: '', busyUuid: null, announcement: '' });
  const drop = (uuid: string) => box.state.approvals.filter((a) => a.uuid !== uuid);
  return {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    async load() {
      try {
        box.set({ approvals: (await api.sessionApprovals()).approvals ?? [], error: '' });
      } catch (error) {
        box.set({ error: flowErrorMessage(error, 'Pending sign-in approvals could not be loaded.') });
      }
    },
    async approve(uuid, proof, match) {
      try {
        await api.approveSessionApproval(uuid, proof, match);
      } catch (error) {
        if (accountErrorCode(error) !== 'wrong_number') throw error;
        const message = accountErrorMessage(error, SESSION_APPROVAL_WRONG_NUMBER);
        box.set({ approvals: drop(uuid), error: message, announcement: message });
        return 'wrong_number';
      }
      box.set({ approvals: drop(uuid), error: '', announcement: 'Sign-in approved.' });
      return 'approved';
    },
    async deny(uuid) {
      if (box.state.busyUuid) return;
      box.set({ busyUuid: uuid, error: '' });
      try {
        await api.denySessionApproval(uuid);
        box.set({ approvals: drop(uuid), busyUuid: null, announcement: 'Sign-in denied. If this wasn’t you, change your password.' });
      } catch (error) {
        box.set({ busyUuid: null, error: flowErrorMessage(error, 'The sign-in could not be denied.') });
      }
    },
    setBusy(uuid) {
      box.set({ busyUuid: uuid });
    },
  };
}
